'use client';

import { useEffect, useRef, useState } from 'react';

/*
  Custom hook that checks if a referenced element is in the viewport
  and if it has been scrolled past. It returns a ref to be attached to
  the element, a boolean indicating whether the element is currently
  visible in the viewport, and a boolean indicating if it has been
  scrolled past.
*/
export const useIsInViewport = <T extends HTMLElement = HTMLDivElement>() => {
  const [isInViewport, setIsInViewport] = useState<boolean>(false);
  const [hasScrolledPast, setHasScrolledPast] = useState<boolean>(false);
  const ref = useRef<T | null>(null);

  const checkIfInViewport = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const inViewport = rect.bottom > 0 && rect.top < window.innerHeight;
      setIsInViewport(inViewport);
      setHasScrolledPast(rect.top < 0);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', checkIfInViewport);
    window.addEventListener('resize', checkIfInViewport);
    checkIfInViewport();

    return () => {
      window.removeEventListener('scroll', checkIfInViewport);
      window.removeEventListener('resize', checkIfInViewport);
    };
  }, []);

  return { ref, isInViewport, hasScrolledPast };
};
